import cx from 'classnames';
import React, { useId } from 'react';
import { Controller } from 'react-hook-form';

import {
	Button,
	Grid,
	GridCol,
	Input,
	Popup,
	PopupSection,
	Text,
	Textarea,
} from '@flixbus/honeycomb-react';

import LocationSearch from 'common/LocationSearch';
import OverlappingBanner from 'common/OverlappingActivitiesBanner/OverlappingBanner';
import WATooLongShiftErrorBanner from 'common/WATooLongShiftErrorBanner';

import { MODAL_TRAVEL_TO_LOCATION_TEXT } from './text';
import { useModalTravelToLocation } from './useModalTravelToLocation';

import styles from './styles.module.css';

const ModalTravelToLocation = ({ readOnly = false, ...props }) => {
	const fromId = useId();
	const fromTimeId = useId();
	const tillId = useId();
	const tillTimeId = useId();
	const noteId = useId();

	const {
		control,
		miDay,
		minTillTime,
		maxTillTime,
		loading,
		isSubmitDisabled,
		isFromTimeDisabled,
		isTillDisabled,
		isTillTimeDisabled,
		hasOverlappingActivities,
		overlappingActivities,
		overlappingAction,
		isDelayOptionAvailable,
		cannotCancelOverlaps,
		hasShiftTimeTooLongError,
		isValidDate,
		validateYear,
		validateTillTime,
		handleFromDateChange,
		handleFromTimeChange,
		handleTillDateChange,
		handleTillTimeChange,
		setOverlappingAction,
		delayMinutes,
		delayError,
		minDelayMinutes,
		setDelayMinutes,
		handleResetSingleField,
		handleSubmit,
		handleClose,
		setValue,
	} = useModalTravelToLocation({ readOnly, ...props });

	return (
		<Popup
			extraClasses={styles.modalWrapper}
			id="modal-travel"
			active={true}
			onOverlayClick={handleClose}
		>
			<PopupSection type="title">
				{readOnly
					? MODAL_TRAVEL_TO_LOCATION_TEXT.travelingDetails
					: MODAL_TRAVEL_TO_LOCATION_TEXT.travelToLocation}
			</PopupSection>

			<PopupSection type="content" extraClasses={styles.scrollableContent}>
				{!readOnly && (
					<Text extraClasses={styles.descriptionMargin}>
						{MODAL_TRAVEL_TO_LOCATION_TEXT.travelPopupDescription}
					</Text>
				)}

				<form
					onSubmit={(e) => {
						e.preventDefault();
						handleSubmit();
					}}
				>
					<div className={styles.timeRow}>
						<div className={styles.timeWrapper}>
							<Controller
								name="from"
								control={control}
								rules={{
									required: true,
									validate: { year: validateYear, range: isValidDate },
								}}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={fromId}
										type="date"
										label={MODAL_TRAVEL_TO_LOCATION_TEXT.startDateAndTime}
										readOnly={readOnly}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										extraClasses={styles.dateInput}
										onChange={(e) => handleFromDateChange(field, e)}
									/>
								)}
							/>
							<Controller
								name="fromTime"
								control={control}
								rules={{ required: true }}
								disabled={isFromTimeDisabled}
								render={({ field: { ref: _ref, ...field } }) => (
									<Input
										{...field}
										id={fromTimeId}
										type="time"
										readOnly={readOnly}
										aria-label="Start time"
										extraClasses={styles.timeInput}
										onChange={(e) => handleFromTimeChange(field, e)}
									/>
								)}
							/>
						</div>

						<div className={styles.timeWrapper}>
							<Controller
								name="till"
								control={control}
								rules={{
									required: true,
									validate: { year: validateYear, range: isValidDate },
								}}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={tillId}
										type="date"
										min={miDay}
										disabled={isTillDisabled}
										readOnly={readOnly}
										label={MODAL_TRAVEL_TO_LOCATION_TEXT.endDateAndTime}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										extraClasses={styles.dateInput}
										onChange={(e) => handleTillDateChange(field, e)}
									/>
								)}
							/>
							<Controller
								name="tillTime"
								control={control}
								rules={{
									required: true,
									validate: validateTillTime,
								}}
								disabled={isTillTimeDisabled}
								render={({ field: { ref: _ref, ...field }, fieldState }) => (
									<Input
										{...field}
										id={tillTimeId}
										type="time"
										readOnly={readOnly}
										aria-label="End time"
										placeholder="hh:mm"
										extraClasses={styles.timeInput}
										min={minTillTime}
										max={maxTillTime}
										valid={!fieldState.invalid}
										infoError={fieldState.error?.message}
										onChange={(e) => handleTillTimeChange(field, e)}
									/>
								)}
							/>
						</div>
					</div>

					<div className={cx(styles.row, styles.full)}>
						<Controller
							name="fromLocation"
							control={control}
							rules={{ required: 'From location is required' }}
							render={({ field }) => (
								<LocationSearch
									readOnly={readOnly}
									label={MODAL_TRAVEL_TO_LOCATION_TEXT.fromLocation}
									onReset={handleResetSingleField}
									fieldControl={field}
									setValue={setValue}
									fieldToReset="fromLocation"
									searchOnlyWithStations={true}
								/>
							)}
						/>
						<Controller
							name="toLocation"
							control={control}
							rules={{ required: 'To location is required' }}
							render={({ field }) => (
								<LocationSearch
									readOnly={readOnly}
									label={MODAL_TRAVEL_TO_LOCATION_TEXT.toLocation}
									onReset={handleResetSingleField}
									fieldControl={field}
									setValue={setValue}
									fieldToReset="toLocation"
									placeholder="e.g. Dallas"
									searchOnlyWithStations={true}
								/>
							)}
						/>
					</div>

					<Grid gutterSize="4">
						<GridCol size={12} extraClasses={styles.gridItem}>
							<Controller
								name="note"
								control={control}
								rules={{ required: false }}
								render={({ field: { ref: _ref, ...field } }) => (
									<Textarea
										{...field}
										id={noteId}
										readOnly={readOnly}
										label="Note"
										placeholder="Additional information"
										onChange={(e) => field.onChange(e.target.value)}
									/>
								)}
							/>
						</GridCol>
					</Grid>

					{hasShiftTimeTooLongError && <WATooLongShiftErrorBanner />}

					{hasOverlappingActivities && (
						<OverlappingBanner
							overlappingList={overlappingActivities}
							overlappingAction={overlappingAction}
							onOverlappingActionChange={setOverlappingAction}
							cannotCancelOverlaps={cannotCancelOverlaps}
							isDelayOptionAvailable={isDelayOptionAvailable}
							delayMinutes={delayMinutes}
							onDelayMinutesChange={setDelayMinutes}
							delayError={delayError}
							minDelayMinutes={minDelayMinutes}
						/>
					)}
				</form>
			</PopupSection>

			<PopupSection type="actions" extraClasses={styles.gridItem}>
				<Button onClick={handleClose}>
					{readOnly
						? MODAL_TRAVEL_TO_LOCATION_TEXT.closeBtn
						: MODAL_TRAVEL_TO_LOCATION_TEXT.cancelBtn}
				</Button>

				{!readOnly && (
					<Button
						type="submit"
						appearance="primary"
						disabled={isSubmitDisabled}
						onClick={handleSubmit}
						loading={loading}
					>
						{MODAL_TRAVEL_TO_LOCATION_TEXT.confirmBtn}
					</Button>
				)}
			</PopupSection>
		</Popup>
	);
};

export default ModalTravelToLocation;
